import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Load cart from localStorage on mount
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('feelwell-cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('feelwell-cart', JSON.stringify(cart));
  }, [cart]);

  // Add item to cart
  const addToCart = (product, quantity = 1, color = null) => {
    setCart((prevCart) => {
      // Check if item with same id AND color already exists
      const existingItem = prevCart.find(
        (item) => item.id === product.id && item.color === color
      );
      
      // Parse price to ensure it's always a number
      const numericPrice = typeof product.price === 'string' 
        ? parseFloat(product.price.replace('$', ''))
        : product.price;
      
      if (existingItem) {
        // Update quantity if item with same id and color already exists
        return prevCart.map((item) =>
          item.id === product.id && item.color === color
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item
        return [
          ...prevCart,
          {
            id: product.id,
            title: product.title,
            price: numericPrice,
            image: product.images[0],
            category: product.category,
            quantity: quantity,
            color: color,
          },
        ];
      }
    });
  };

  // Remove item from cart
  const removeFromCart = (productId, color = null) => {
    setCart((prevCart) => 
      prevCart.filter((item) => !(item.id === productId && item.color === color))
    );
  };

  // Update item quantity
  const updateQuantity = (productId, quantity, color = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, color);
      return;
    }
    
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId && item.color === color ? { ...item, quantity } : item
      )
    );
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
  };

  // Get total items count
  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Get total price
  const getTotalPrice = () => {
    return cart.reduce((total, item) => {
      const price = typeof item.price === 'string' 
        ? parseFloat(item.price.replace('$', ''))
        : item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalItems,
    getTotalPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
